import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {
  CalendarMonth,
  CancelOutlined,
  CheckCircleOutline,
  EventAvailable,
  GroupsOutlined,
  HourglassTop,
  PeopleAltOutlined,
  PersonOffOutlined,
  Refresh
} from "@mui/icons-material";
import api from "../../services/api";
import AcessoRestrito from "../../components/AcessoRestrito";

const hojeLocal = () => {
  const agora = new Date();
  const ano = agora.getFullYear();
  const mes = String(agora.getMonth() + 1).padStart(2, "0");
  const dia = String(agora.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

const formatarHorario = (valor) => valor
  ? new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo"
  }).format(new Date(valor))
  : "—";

const formatarDataCurta = (valor) => valor
  ? new Intl.DateTimeFormat("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Sao_Paulo"
  }).format(new Date(valor))
  : "";

const coresStatus = {
  Agendado: "info",
  Confirmado: "success",
  Concluido: "success",
  Cancelado: "error",
  Faltou: "warning"
};

function KpiCard({ titulo, valor, detalhe, icon, color = "primary" }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">{titulo}</Typography>
            <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800 }}>{valor}</Typography>
            {detalhe && <Typography variant="caption" color="text.secondary">{detalhe}</Typography>}
          </Box>
          <Box sx={{
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            color: `${color}.main`,
            bgcolor: `${color}.main`,
            backgroundImage: "linear-gradient(rgba(255,255,255,.9), rgba(255,255,255,.9))",
            backgroundBlendMode: "screen"
          }}>
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Grid item xs={12} sm={6} lg={4} xl={2} key={index}>
            <Skeleton variant="rounded" height={132} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" height={330} />
      <Skeleton variant="rounded" height={250} />
    </Stack>
  );
}

export default function Dashboard() {
  const [dataReferencia, setDataReferencia] = useState(hojeLocal);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async ({ silencioso = false } = {}) => {
    try {
      if (silencioso) setAtualizando(true);
      else setLoading(true);
      setErro("");
      const response = await api.get("/dashboard/resumo", { params: { data: dataReferencia } });
      setDados(response.data);
    } catch (error) {
      setErro(error.response?.data?.error || "Não foi possível carregar a visão operacional.");
    } finally {
      setLoading(false);
      setAtualizando(false);
    }
  }, [dataReferencia]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const atualizarSeVisivel = () => {
      if (document.visibilityState === "visible") carregar({ silencioso: true });
    };
    const timer = window.setInterval(atualizarSeVisivel, 60000);
    return () => window.clearInterval(timer);
  }, [carregar]);

  const agendaPorDia = useMemo(() => {
    const grupos = new Map();
    (dados?.agendaSemanal || []).forEach((item) => {
      const chave = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Sao_Paulo"
      }).format(new Date(item.inicio));
      if (!grupos.has(chave)) grupos.set(chave, []);
      grupos.get(chave).push(item);
    });

    if (!dados?.periodo?.inicioSemana) return [];
    const [ano, mes, dia] = dados.periodo.inicioSemana.split("-").map(Number);
    const inicio = new Date(ano, mes - 1, dia, 12);
    return Array.from({ length: 7 }, (_, indice) => {
      const data = new Date(inicio);
      data.setDate(inicio.getDate() + indice);
      const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
      return { chave, data, itens: grupos.get(chave) || [] };
    });
  }, [dados]);

  const agenda = dados?.agenda || {};
  const atendimento = dados?.atendimento || {};
  const indicadores = dados?.indicadores || {};
  const taxaPresenca = indicadores.taxaPresenca === null || indicadores.taxaPresenca === undefined
    ? "—"
    : `${indicadores.taxaPresenca.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
  const fontesIndisponiveis = dados?.fontesIndisponiveis || [];

  return (
    <AcessoRestrito modulo="dashboard">
      <Box sx={{ minHeight: "100%", p: { xs: 2, md: 3 }, bgcolor: "background.default" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>Visão operacional</Typography>
            <Typography color="text.secondary">Agenda e atendimentos em um único painel.</Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              component="input"
              type="date"
              value={dataReferencia}
              onChange={(event) => setDataReferencia(event.target.value)}
              aria-label="Data de referência do dashboard"
              sx={{
                height: 42,
                px: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                color: "text.primary",
                bgcolor: "background.paper",
                font: "inherit"
              }}
            />
            <Tooltip title="Atualizar agora">
              <span>
                <IconButton onClick={() => carregar({ silencioso: true })} disabled={atualizando}>
                  {atualizando ? <CircularProgress size={22} /> : <Refresh />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {erro && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={<Button color="inherit" size="small" onClick={() => carregar()}>Tentar novamente</Button>}
          >
            {erro}
          </Alert>
        )}

        {!erro && fontesIndisponiveis.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Alguns dados estão temporariamente indisponíveis. O painel continuará atualizando automaticamente.
          </Alert>
        )}

        {loading && !dados ? <DashboardSkeleton /> : (
          <Stack spacing={3}>
            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Agenda do dia</Typography>
                  <Typography variant="body2" color="text.secondary">Volume e situação dos compromissos.</Typography>
                </Box>
                <Chip
                  variant="outlined"
                  color="secondary"
                  icon={<GroupsOutlined />}
                  label={`${indicadores.profissionaisAtivos || 0} profissionais ativos`}
                />
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={3}>
                  <KpiCard titulo="Agendamentos" valor={agenda.totalHoje || 0} detalhe={`${agenda.agendados || 0} aguardando confirmação`} icon={<CalendarMonth />} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <KpiCard titulo="Confirmados" valor={agenda.confirmados || 0} detalhe="Presença confirmada" icon={<EventAvailable />} color="success" />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <KpiCard titulo="Cancelados" valor={agenda.cancelados || 0} detalhe="Na data selecionada" icon={<CancelOutlined />} color="error" />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <KpiCard titulo="Faltaram" valor={agenda.faltaram || 0} detalhe="Não compareceram" icon={<PersonOffOutlined />} color="warning" />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Fluxo de atendimento</Typography>
                <Typography variant="body2" color="text.secondary">Acompanhe a operação em tempo real.</Typography>
              </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard titulo="Aguardando" valor={atendimento.aguardando || 0} detalhe={`${atendimento.chamando || 0} chamando`} icon={<HourglassTop />} color="warning" />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard titulo="Em atendimento" valor={atendimento.emAtendimento || 0} detalhe="Agora" icon={<PeopleAltOutlined />} color="info" />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard titulo="Atendidos" valor={atendimento.atendidos || 0} detalhe={`${atendimento.desistentes || 0} desistentes`} icon={<CheckCircleOutline />} color="success" />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard titulo="Taxa de presença" valor={taxaPresenca} detalhe="Atendimentos encerrados no dia" icon={<EventAvailable />} color="success" />
              </Grid>
            </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 750 }}>Agenda semanal</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dados?.periodo?.inicioSemana} a {dados?.periodo?.fimSemana}
                      </Typography>
                    </Box>
                    <Chip size="small" label={`${dados?.agendaSemanal?.length || 0} compromissos`} />
                  </Stack>
                  <Box sx={{ overflowX: "auto", pb: 1 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(150px, 1fr))", gap: 1.25, minWidth: 1050 }}>
                      {agendaPorDia.map((dia) => (
                        <Box key={dia.chave}>
                          <Typography variant="subtitle2" sx={{ mb: 1, textTransform: "capitalize" }}>
                            {new Intl.DateTimeFormat("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" }).format(dia.data)}
                          </Typography>
                          <Stack spacing={1}>
                            {dia.itens.length === 0 && (
                              <Box sx={{ p: 1.5, border: "1px dashed", borderColor: "divider", borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary">Sem agendamentos</Typography>
                              </Box>
                            )}
                            {dia.itens.map((item) => (
                              <Box key={item.id} sx={{ p: 1.25, borderRadius: 2, bgcolor: "action.hover", borderLeft: 3, borderColor: `${coresStatus[item.status] || "primary"}.main` }}>
                                <Typography variant="caption" sx={{ fontWeight: 800 }}>{formatarHorario(item.inicio)}</Typography>
                                <Typography variant="body2" noWrap title={item.cliente}>{item.cliente}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap title={item.servico}>{item.servico}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 750 }}>Próximos atendimentos</Typography>
                  <Typography variant="caption" color="text.secondary">Compromissos agendados ou confirmados para a data selecionada.</Typography>
                </Box>
                <Chip size="small" label={`${dados?.proximosAtendimentos?.length || 0} próximos`} />
              </Stack>
              {dados?.proximosAtendimentos?.length ? (
                <Grid container spacing={1.5}>
                  {dados.proximosAtendimentos.map((item) => (
                    <Grid item xs={12} sm={6} lg={3} key={item.id}>
                      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                        <CardContent>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography sx={{ fontWeight: 800 }}>{formatarHorario(item.inicio)}</Typography>
                            <Chip size="small" color={coresStatus[item.status] || "default"} label={item.status} />
                          </Stack>
                          <Typography sx={{ mt: 1.5, fontWeight: 700 }} noWrap>{item.cliente}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>{item.servico}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.colaborador} · {item.local}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", mt: 1 }}>{formatarDataCurta(item.inicio)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <CalendarMonth sx={{ fontSize: 42, color: "text.disabled" }} />
                  <Typography color="text.secondary">Nenhum próximo atendimento para esta data.</Typography>
                </Box>
              )}
            </Paper>

            <Typography variant="caption" color="text.secondary" textAlign="right">
              Atualizado em {dados?.atualizadoEm ? new Date(dados.atualizadoEm).toLocaleString("pt-BR") : "—"}
            </Typography>
          </Stack>
        )}
      </Box>
    </AcessoRestrito>
  );
}
